import React from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidatingInput, ValidatingTextArea, ValidationScope } from '@/components/forms/validation';
import { getInputValue } from '@/utils/html';
import ItemCategorySelect from '@/components/item-categories/ItemCategorySelect';
import { BaseProduct, ProductType } from '@/types';
import MandatoryIndicator from '@/components/forms/MandatoryIndicator';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';
import theme from './EditProductWizard.module.scss';
import SuggestInput from '@/components/SuggestInput';

interface EditProductWizardStepMainProps {
    product: BaseProduct;
    onChange: (product: BaseProduct) => void;
    suppliers: string[];
}

const EditProductWizardStepMain: React.FC<EditProductWizardStepMainProps> = ({ product, onChange, suppliers }) => {
    const handleInputChange = (e) => {
        onChange({ ...product, [e.target.name]: getInputValue(e.target) });
    };

    return (
        <ValidationScope>
            <div className="form columns-2">
                <div>
                    <label>
                        <div className={theme.titleContainer}>
                            <Trans>Name</Trans>
                            <MandatoryIndicator />
                            <InfoIconWithTooltip content={<Trans>Product name that the buyers will see</Trans>} />
                        </div>

                        <ValidatingInput
                            className="form-control"
                            autoFocus
                            type="text"
                            value={product.name}
                            onChange={handleInputChange}
                            name="name"
                            required
                            placeholder={t`Name`}
                        />
                    </label>

                    <label>
                        <div className={theme.titleContainer}>
                            <Trans>Supplier</Trans>
                        </div>

                        <SuggestInput
                            onChange={(newValue) => {
                                onChange({ ...product, supplier: newValue });
                            }}
                            existingValues={suppliers}
                            value={product.supplier}
                            placeholder={t`Supplier`}
                        />
                    </label>

                    <label>
                        <div className={theme.titleContainer}>
                            <Trans>Description</Trans>
                            <InfoIconWithTooltip content={<Trans>Product description that the buyers will see</Trans>} />
                        </div>

                        <ValidatingTextArea
                            className="form-control"
                            value={product.description}
                            onChange={handleInputChange}
                            name="description"
                            autoResize
                            minRows={5}
                            maxRows={10}
                            placeholder={t`Description`}
                        />
                    </label>
                </div>

                <div>
                    <label>
                        <div className={theme.titleContainer}>
                            <Trans>Category</Trans>
                            <MandatoryIndicator />
                        </div>

                        <ItemCategorySelect
                            isDisabled={product.type === ProductType.CustomPaint}
                            className="form-control-unstyled"
                            value={product.itemCategoryId}
                            onChange={(nv) => onChange({ ...product, itemCategoryId: nv.value })}
                            required
                            fieldName={t`Category`}
                            showNoneOption={false}
                        />
                    </label>

                    <label>
                        <div className={theme.titleContainer}>
                            <Trans>Product Link</Trans>
                            <InfoIconWithTooltip
                                content={
                                    <Trans>
                                        Link to an external page with more info about the product either in your homepage or
                                        the manufacturers
                                    </Trans>
                                }
                            />
                        </div>

                        <ValidatingInput
                            className="form-control"
                            type="text"
                            value={product.productLink}
                            onChange={handleInputChange}
                            name="productLink"
                            placeholder={t`https://www.example.com`}
                        />
                    </label>

                    <label>
                        <div className={theme.titleContainer}>
                            <Trans>Comments for site</Trans>
                            <InfoIconWithTooltip
                                content={
                                    <Trans>
                                        This comment can be included in exports. Buyers will not see this information.
                                    </Trans>
                                }
                            />
                        </div>

                        <ValidatingTextArea
                            className="form-control"
                            value={product.commentForSite}
                            onChange={handleInputChange}
                            name="commentForSite"
                            autoResize
                            minRows={5}
                            maxRows={10}
                            placeholder={t`Comments for site`}
                        />
                    </label>
                </div>
            </div>
        </ValidationScope>
    );
};

export default EditProductWizardStepMain;
